/**
 * The request form's building blocks: a compact labelled row (the draft workspace's pattern) and a
 * choice select over the proxy's own vocabulary. The person picker is `PersonCombobox`. A problem the proxy reported shows under its field.
 */
import type { ReactNode } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@finn-auto/design-system/atoms/select';
import { cn } from '../../../../shared';
import { choiceLabel } from './requestForm';

/**
 * The form's error lines. A darker red than the FINN token: `red` (#E71D3F) on the grey step boxes
 * is 4.07:1, under the 4.5:1 small text needs; this is ~5.9:1. The one non-token colour here.
 */
export const ERROR_TEXT = 'text-xs text-[#B91C1C]';

/** A question on the left, its control on the right, and the proxy's problem with it underneath (`<id>-error`). */
export function FieldRow({ id, label, hint, error, children }: { id: string; label: string; hint?: string; error?: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-1 items-center gap-1 sm:grid-cols-[minmax(0,1fr)_17rem] sm:gap-3">
      <div>
        <p className="text-sm font-semibold text-zinc-900">{label}</p>
        {hint && <p className="text-sm text-zinc-700">{hint}</p>}
      </div>
      <div>
        {children}
        {error && <p id={`${id}-error`} className={`mt-1 ${ERROR_TEXT}`}>{error}</p>}
      </div>
    </div>
  );
}

/** One of the proxy's choices, shown with its readable label; the value sent is Airtable's own. */
export function ChoiceSelect({
  label, value, options, error, errorId, onChange,
}: { label: string; value: string | null; options: string[]; error?: string; errorId: string; onChange: (v: string) => void }) {
  return (
    <Select value={value ?? undefined} onValueChange={onChange}>
      <SelectTrigger
        size="md"
        aria-label={label}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={cn('w-full rounded', error && 'border-red')}
      >
        <SelectValue placeholder="Choose" />
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o} value={o}>
            {choiceLabel(o)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * A numbered step of the form: a grey box on the white page (David, Oct 13 2026 — white boxes on a
 * white page left nothing to tell the sections apart), so the white controls inside stand out.
 */
export function FormSection({
  step, title, locked, children,
}: { step: number; title: string; locked?: string; children: ReactNode }) {
  const headingId = `step-${step}`;
  const lockId = `${headingId}-locked`;
  return (
    <section aria-labelledby={headingId} aria-describedby={locked ? lockId : undefined} data-locked={locked ? true : undefined} className="flex flex-col gap-4 rounded bg-cotton p-5">
      <h2 id={headingId} className={cn('flex items-center gap-3 text-base font-semibold', locked ? 'text-zinc-500' : 'text-zinc-900')}>
        <span aria-hidden className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm text-white', locked ? 'bg-zinc-400' : 'bg-zinc-900')}>{step}</span>
        <span><span className="sr-only">Step {step}: </span>{title}</span>
      </h2>
      {locked && <p id={lockId} className="text-sm text-zinc-700">{locked}</p>}
      {/* Locked = shown but unusable (David, Oct 13 2026): a disabled fieldset disables every
          control inside it, the design-system ones included, so a later step cannot be answered
          before the answers it depends on. Unnamed on purpose: the section around it already
          carries the heading, and naming both would have it read twice. */}
      <fieldset disabled={Boolean(locked)} className={cn('m-0 flex min-w-0 flex-col gap-4 border-0 p-0', locked && 'opacity-50')}>
        {children}
      </fieldset>
    </section>
  );
}
